import { HttpClient } from '@shared/adapters/HttpClient';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductDimensionsApiAdapter } from '../../adapters/ProductDimensionsApiAdapter';

interface ProductDetails {
  id: number;
  name: string;
  description?: string | null;
  code: string;
  cost?: number | null;
  category_id?: number | null;
  image_hash?: string | null;
  length?: string | null;
  heigth?: string | null;
  width?: string | null;
  weight?: string | null;
  is_box?: boolean | null;
  batch_active?: boolean | null;
  batch_type?: string | null;
  weight_unit?: string | null;
  dimension_unit?: string | null;
}

export interface ProductDimensionsModalRef {
  open: (productId: number, onCompleted?: () => Promise<void> | void) => void;
}

interface ProductDimensionsModalProps {
  httpClient: HttpClient;
}

interface MissingFields {
  category: boolean;
  length: boolean;
  heigth: boolean;
  width: boolean;
  weight: boolean;
}

const EMPTY_MISSING_FIELDS: MissingFields = {
  category: true,
  length: true,
  heigth: true,
  width: true,
  weight: true,
};

const hasPositiveValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  if (!normalized) return false;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0;
};

const isPositiveNumber = (value: string): boolean => {
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) && parsed > 0;
};

const hasValidCategoryId = (value: string): boolean => {
  const normalized = value.trim();
  if (!normalized) return false;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0;
};

const getMissingFields = (product: ProductDetails): MissingFields => ({
  category: !hasValidCategoryId(String(product.category_id ?? '')),
  length: !hasPositiveValue(product.length),
  heigth: !hasPositiveValue(product.heigth),
  width: !hasPositiveValue(product.width),
  weight: !hasPositiveValue(product.weight),
});

export const ProductDimensionsModal = forwardRef<ProductDimensionsModalRef, ProductDimensionsModalProps>(
  ({ httpClient }, ref) => {
    const { t } = useTranslation('merchandiseV3');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);

    const [productId, setProductId] = useState<number | null>(null);
    const [productDetails, setProductDetails] = useState<ProductDetails | null>(null);
    const [length, setLength] = useState('');
    const [heigth, setHeigth] = useState('');
    const [width, setWidth] = useState('');
    const [weight, setWeight] = useState('');
    const [isBox, setIsBox] = useState(false);
    const [categoryId, setCategoryId] = useState('');
    const [weightUnit, setWeightUnit] = useState('g');
    const [dimensionUnit, setDimensionUnit] = useState('cm');
    const [isLoading, setIsLoading] = useState(false);
    const [missingFields, setMissingFields] = useState<MissingFields>(EMPTY_MISSING_FIELDS);

    const { data: categories = [], isLoading: loadingCategories } = useQuery({
      queryKey: ['product-dimensions-categories'],
      queryFn: () => new ProductDimensionsApiAdapter(httpClient).getCategories(),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });

    const onCompletedRef = useRef<(() => Promise<void> | void) | null>(null);

    const selectedCategory = useMemo(
      () => categories.find((category) => category.id === categoryId),
      [categories, categoryId],
    );

    const willActivateBatchManagement =
      Boolean(selectedCategory?.batch_required) && !productDetails?.batch_active && missingFields.category;

    const isFormValid = useMemo(() => {
      if (missingFields.category && !hasValidCategoryId(categoryId)) return false;
      if (missingFields.length && !isPositiveNumber(length)) return false;
      if (missingFields.heigth && !isPositiveNumber(heigth)) return false;
      if (missingFields.width && !isPositiveNumber(width)) return false;
      if (missingFields.weight && !isPositiveNumber(weight)) return false;
      return true;
    }, [categoryId, heigth, length, missingFields, weight, width]);

    useImperativeHandle(ref, () => ({
      open: (id: number, onCompleted?: () => Promise<void> | void) => {
        setProductId(id);
        onCompletedRef.current = onCompleted || null;
        setIsLoading(true);
        // Limpiar estado previo
        setProductDetails(null);
        setLength('');
        setHeigth('');
        setWidth('');
        setWeight('');
        setIsBox(false);
        setCategoryId('');
        setWeightUnit('g');
        setDimensionUnit('cm');
        setMissingFields(EMPTY_MISSING_FIELDS);

        const adapter = new ProductDimensionsApiAdapter(httpClient);

        adapter
          .getById(id)
          .then((data) => {
            const details: ProductDetails = {
              id: data.id,
              name: data.name,
              description: data.description,
              code: data.code,
              cost: data.cost,
              category_id: data.category_id,
              image_hash: data.image_hash,
              length: data.length,
              heigth: data.heigth,
              width: data.width,
              weight: data.weight,
              is_box: data.is_box,
              batch_active: data.batch_active,
              batch_type: data.batch_type,
              weight_unit: data.weight_unit,
              dimension_unit: data.dimension_unit,
            };
            setProductDetails(details);
            setMissingFields(getMissingFields(details));
            setLength(details.length || '');
            setHeigth(details.heigth || '');
            setWidth(details.width || '');
            setWeight(details.weight || '');
            setIsBox(details.is_box || false);
            setCategoryId(hasValidCategoryId(String(details.category_id ?? '')) ? String(details.category_id) : '');
            const hasSavedWeight = (() => {
              if (!details.weight) return false;
              const parsed = Number(String(details.weight).trim());
              return Number.isFinite(parsed) && parsed > 0;
            })();
            setWeightUnit(hasSavedWeight ? details.weight_unit || 'g' : 'g');
            setDimensionUnit(details.dimension_unit || 'cm');
            modalRef.current?.openModal();
          })
          .catch((error: any) => {
            console.log('🔍 ProductDimensionsModal - Error al cargar producto:', error);
            const message =
              error?.response?.data?.error?.message ||
              error?.response?.data?.message ||
              error?.message ||
              t('receive.dimensions.load_error', 'No se pudo cargar la información del producto. Inténtalo de nuevo.');

            toast({
              title: t('receive.dimensions.load_error_title', 'Error al cargar producto'),
              description: message,
              variant: 'destructive',
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
    }));

    const handleSave = async () => {
      if (!productId || !productDetails) {
        toast({
          title: t('receive.dimensions.load_error_title', 'Error al cargar producto'),
          description: t(
            'receive.dimensions.load_error',
            'No se pudo cargar la información del producto. Inténtalo de nuevo.',
          ),
          variant: 'destructive',
        });
        return;
      }

      if (!isFormValid) {
        toast({
          title: t('receive.dimensions.validation_error', 'Faltan datos'),
          description: t(
            'receive.dimensions.required_extended',
            'Completa los campos pendientes con valores mayores que 0 para continuar.',
          ),
          variant: 'destructive',
        });
        return;
      }

      const trimmedLength = length.trim();
      const trimmedHeigth = heigth.trim();
      const trimmedWidth = width.trim();
      const trimmedWeight = weight.trim();

      setIsLoading(true);

      try {
        const adapter = new ProductDimensionsApiAdapter(httpClient);

        await adapter.updateDimensions(productId, productDetails, {
          length: missingFields.length ? trimmedLength : String(productDetails.length ?? trimmedLength),
          heigth: missingFields.heigth ? trimmedHeigth : String(productDetails.heigth ?? trimmedHeigth),
          width: missingFields.width ? trimmedWidth : String(productDetails.width ?? trimmedWidth),
          weight: missingFields.weight ? trimmedWeight : String(productDetails.weight ?? trimmedWeight),
          is_box: isBox,
          category_id: missingFields.category ? categoryId : String(productDetails.category_id ?? categoryId),
          weight_unit: weightUnit || 'g',
          dimension_unit: dimensionUnit || productDetails.dimension_unit || 'cm',
        });

        toast({
          title: t('receive.dimensions.success_title', 'Producto actualizado'),
          description: t(
            'receive.dimensions.success_desc',
            'Las dimensiones del producto se actualizaron correctamente.',
          ),
          variant: 'default',
        });

        if (onCompletedRef.current) {
          await onCompletedRef.current();
        }

        modalRef.current?.closeModal();
      } catch (error: any) {
        console.log('🔍 ProductDimensionsModal - Error al actualizar producto:', error);
        const data = error?.response?.data || error?.data;
        let message =
          data?.error?.message ||
          data?.message ||
          error?.message ||
          t('receive.dimensions.update_error', 'Error al actualizar el producto. Inténtalo de nuevo.');

        if (data?.errors && typeof data.errors === 'object') {
          const lines = Object.entries(data.errors).map(([field, msgs]) => {
            if (Array.isArray(msgs)) return `${field}: ${msgs.join(', ')}`;
            if (typeof msgs === 'string') return `${field}: ${msgs}`;
            if (msgs && typeof msgs === 'object') {
              return `${field}: ${Object.values(msgs as Record<string, unknown>)
                .map((v) => String(v))
                .join(', ')}`;
            }
            return String(field);
          });
          if (lines.length > 0) {
            message = lines.join('\n');
          }
        }

        toast({
          title: t('receive.dimensions.update_error_title', 'Error al actualizar el producto'),
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    const buildImageUrl = (imageHash?: string | null): string => {
      if (!imageHash) return '';
      if (imageHash.startsWith('http')) return imageHash;
      return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('receive.dimensions.modal_title', 'Completar dimensiones del producto')}
        type="info"
        width="md"
      >
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          {productDetails && (
            <div className="mb-2">
              <Product
                id={productDetails.id}
                name={productDetails.name}
                code={productDetails.code}
                src={buildImageUrl(productDetails.image_hash)}
                showImageModal={true}
                enableLineClamp={true}
              />
            </div>
          )}

          <p className="text-sm text-complementary-text">
            {t(
              'receive.dimensions.description',
              'Antes de recepcionar este producto debes completar los datos pendientes. Estos datos se utilizarán para cálculos logísticos.',
            )}
          </p>

          <div className="space-y-4">
            {missingFields.category && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="product-category" className="pb-2">
                  {t('receive.dimensions.category', 'Categoría')}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Combobox
                  options={categories.map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                  }))}
                  placeholder={
                    loadingCategories
                      ? t('receive.dimensions.loading_categories', 'Cargando categorías...')
                      : t('receive.dimensions.category_placeholder', 'Selecciona una categoría')
                  }
                  value={categoryId}
                  setValue={(value) => setCategoryId(String(value))}
                  dataTestId="product-dimensions-category-combobox"
                  disabled={isLoading || loadingCategories}
                />
              </div>
            )}

            {willActivateBatchManagement && (
              <Alert
                className="bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
                data-testid="product-dimensions-batch-required-alert"
              >
                <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <AlertDescription className="text-sm text-orange-800 dark:text-orange-200">
                  {t(
                    'receive.dimensions.batch_required_alert',
                    'Al asignar esta categoría se activará automáticamente la gestión por lotes en el producto.',
                  )}
                </AlertDescription>
              </Alert>
            )}

            {(missingFields.weight || missingFields.length) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {missingFields.weight && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="product-weight" className="flex-1 pb-2">
                        {t('receive.dimensions.weight', 'Peso')}
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Select value={weightUnit} onValueChange={setWeightUnit} disabled={isLoading}>
                        <SelectTrigger
                          className="w-12 border-none bg-transparent px-0 py-0 shadow-none h-auto min-w-0"
                          data-testid="product-dimensions-weight-unit-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="g">{t('receive.dimensions.g', 'g')}</SelectItem>
                          <SelectItem value="kg">{t('receive.dimensions.kg', 'kg')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      id="product-weight"
                      type="number"
                      min="0"
                      step="0.01"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      disabled={isLoading}
                      data-testid="product-dimensions-weight-input"
                    />
                  </div>
                )}
                {missingFields.length && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="product-length" className="flex-1 pb-2">
                        {t('receive.dimensions.length', 'Largo')}
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Select value={dimensionUnit} onValueChange={setDimensionUnit} disabled={isLoading}>
                        <SelectTrigger
                          className="w-12 border-none bg-transparent px-0 py-0 shadow-none h-auto min-w-0"
                          data-testid="product-dimensions-dimension-unit-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cm">{t('receive.dimensions.cm', 'cm')}</SelectItem>
                          <SelectItem value="mt">{t('receive.dimensions.mt', 'mt')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      id="product-length"
                      type="number"
                      min="0"
                      step="0.01"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      disabled={isLoading}
                      data-testid="product-dimensions-length-input"
                    />
                  </div>
                )}
              </div>
            )}

            {(missingFields.width || missingFields.heigth) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {missingFields.width && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2 w-full">
                      <Label htmlFor="product-width" className="flex-1 pb-2">
                        {t('receive.dimensions.width', 'Ancho')}
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                    </div>
                    <Input
                      id="product-width"
                      type="number"
                      min="0"
                      step="0.01"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      disabled={isLoading}
                      data-testid="product-dimensions-width-input"
                    />
                  </div>
                )}
                {missingFields.heigth && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2 w-full">
                      <Label htmlFor="product-heigth" className="flex-1 pb-2">
                        {t('receive.dimensions.height', 'Alto')}
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                    </div>
                    <Input
                      id="product-heigth"
                      type="number"
                      min="0"
                      step="0.01"
                      value={heigth}
                      onChange={(e) => setHeigth(e.target.value)}
                      disabled={isLoading}
                      data-testid="product-dimensions-height-input"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('receive.dimensions.cancel', 'Cancelar')}
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading} data-testid="product-dimensions-save">
              {isLoading
                ? t('receive.dimensions.saving', 'Guardando...')
                : t('receive.dimensions.save', 'Guardar y continuar')}
            </Button>
          </div>
        </form>
      </ModalCenter>
    );
  },
);

ProductDimensionsModal.displayName = 'ProductDimensionsModal';
